/**
 * Convert a string to kebab-case (e.g. "someFieldID" -> "some-field-id")
 * @param {string} value - The string to convert
 * @returns {string} The kebab-cased string
 */
export const kebabCase = value =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .toLowerCase()
    .replace(/^-|-$/g, "");

/**
 * Format a number as currency
 * @param {number} value - The numeric value to format as currency
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @param {string} currency - The currency code (default: 'USD')
 * @returns {string} The formatted currency string
 */
export const formatCurrency = (value, options) => {
  // Handle null, undefined, or non-numeric values
  const { locale = "en-US", currency = "USD" } = options || {};

  if (value === null || value === undefined || isNaN(value)) {
    return "$--";
  }

  // Ensure value is a number
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  // safety to to prevent NaN from being formatted
  if (isNaN(numValue)) {
    return "$--";
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options
  }).format(numValue);
};

/**
 * Format a number as a percentage
 * @param {number} value - The numeric value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} The formatted percentage string
 */
export const formatPercentage = (value, decimals = 0) => {
  // Handle null, undefined, or non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return "--%";
  }

  // Ensure value is a number
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  // safety to to prevent NaN from being formatted
  if (isNaN(numValue)) {
    return "--%";
  }

  // Format as percentage
  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Format a number with specified decimal places and thousands separators
 * @param {number} value - The numeric value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} The formatted number string
 */
export const formatNumber = (value, decimals = 0, locale = "en-US") => {
  // Handle null, undefined, or non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }

  // Ensure value is a number
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  // safety to to prevent NaN from being formatted
  if (isNaN(numValue)) {
    return "0";
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numValue);
};
